import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";

import ActionIconButton from "@components/buttons/ActionIconButton";
import DateStamp from "@components/dates/DateStamp";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import styles from "@components/EmployersTable.module.css";
import HandleQuery from "@components/HandleQuery";
import Nbsp from "@components/Nbsp";
import Table, { TableHeader } from "@components/Table";
import TextWithIcon from "@components/text/TextWithIcon";
import InternalLink from "@components/typography/InternalLink";
import useSubscribeToAllEmployers from "@hooks/subscriptions/useSubscribeToAllEmployers";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useOpener from "@hooks/useOpener";
import {
  EligibilityMethodEnum,
  EmployerDestroyDocument,
  EmployerDestroyMutation,
  EmployerDestroyMutationVariables,
  EmployerSortByEnum,
  GetEmployersDocument,
  GetEmployersQuery,
  GetEmployersQueryVariables,
  SortDirectionEnum
} from "@typing/Generated";
import { contractDetailsOnDate } from "@utils/employerContractUtils";

type Employer = NonNullable<NonNullable<NonNullable<GetEmployersQuery["employers"]["edges"]>[number]>["node"]>;

type Props = {
  userIsExpert?: boolean;
};

const EmployersTable = ({ userIsExpert = false }: Props) => {
  const intl = useIntl();
  const deleteOpener = useOpener();

  const [getEmployers, getEmployersQuery] = useLazyQuery<GetEmployersQuery, GetEmployersQueryVariables>(
    GetEmployersDocument
  );

  useSubscribeToAllEmployers(() => {
    getEmployersQuery.refetch();
  });

  const [destroyEmployer, client] = useMutationWithValidation<
    EmployerDestroyMutation,
    EmployerDestroyMutationVariables
  >(EmployerDestroyDocument, "employer", {
    update: () => {
      client.resetStore();
    }
  });

  const [sortBy, setSortBy] = useState<EmployerSortByEnum>(EmployerSortByEnum.NAME);
  const [sortDirection, setSortDirection] = useState<SortDirectionEnum>(SortDirectionEnum.ASCENDING);
  const [employerBeingDeleted, setEmployerBeingDeleted] = useState<Employer | undefined>(undefined);

  useEffect(() => {
    getEmployers({ variables: { sortBy, sortDirection } });
  }, [getEmployers, sortBy, sortDirection]);

  const employers = useNodesFromConnection(getEmployersQuery.data?.employers);

  const generateDeleteStartHandler = useCallback(
    (employer: Employer) => () => {
      setEmployerBeingDeleted(employer);
      deleteOpener.open();
    },
    [deleteOpener]
  );

  const handleDelete = useCallback(() => {
    if (employerBeingDeleted) {
      destroyEmployer({
        id: employerBeingDeleted.id
      });
    }
  }, [destroyEmployer, employerBeingDeleted]);

  const tableHeaders = useMemo<TableHeader<EmployerSortByEnum>[]>(() => {
    const list = [
      { columnSortBy: EmployerSortByEnum.NAME },
      { columnSortBy: EmployerSortByEnum.REFRESH_PERIOD_BEGIN_DATE },
      { columnSortBy: EmployerSortByEnum.REFRESH_PERIOD_END_DATE },
      { messageKey: "pages.admin.employers.tableHeadings.MINUTES_PER_CLIENT" },
      { messageKey: "pages.admin.employers.tableHeadings.EMPLOYEES_PURCHASED" },
      { messageKey: "pages.admin.employers.tableHeadings.CURRENT_BILLABLE_CLIENTS" },
      { columnSortBy: EmployerSortByEnum.CLIENT_COUNT },
      { columnSortBy: EmployerSortByEnum.CREATED_AT },
      { messageKey: "pages.admin.employers.tableHeadings.ELIGIBILITY_METHOD" }
    ];

    if (!userIsExpert) {
      return [...list, { messageKey: "pages.admin.employers.tableHeadings.ACTIONS" }];
    }
    return list;
  }, [userIsExpert]);

  const currentContractData = useCallback((employer: Employer) => {
    if (employer?.currentContract) {
      return contractDetailsOnDate(employer.currentContract, new Date());
    }
  }, []);

  const currentRefreshPeriodEmployeesPurchased = useCallback(
    (employer: Employer): number => {
      if (employer.currentContract?.employeesPurchased) {
        return currentContractData(employer)?.employeesPurchased || 0;
      }
      return 0;
    },
    [currentContractData]
  );

  return (
    <>
      <HandleQuery query={getEmployersQuery}>
        {data => (
          <Table<EmployerSortByEnum>
            fetchMore={getEmployersQuery.fetchMore}
            headerKey="pages.admin.employers.tableHeadings"
            headers={tableHeaders}
            pageInfo={data.employers.pageInfo}
            setSortBy={setSortBy}
            setSortDirection={setSortDirection}
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            {employers.map(employer => (
              <tr key={employer.id}>
                <td>
                  <div className={styles.minWidth}>
                    {userIsExpert ? (
                      <InternalLink to={`/expert/employers/${employer.id}`}>{employer.name}</InternalLink>
                    ) : (
                      <InternalLink to={`/admin/employers/${employer.id}`}>{employer.name}</InternalLink>
                    )}
                  </div>
                </td>
                <td>
                  {employer.currentRefreshPeriod?.startDate && (
                    <DateStamp absoluteDate showWeekday={false} value={employer.currentRefreshPeriod.startDate} />
                  )}
                </td>
                <td>
                  {employer.currentRefreshPeriod?.endDate && (
                    <DateStamp absoluteDate showWeekday={false} value={employer.currentRefreshPeriod.endDate} />
                  )}
                </td>
                <td>
                  {employer.currentContract?.hasUnlimitedMinutes
                    ? intl.formatMessage({ id: "dictionary.unlimited" })
                    : intl.formatNumber(currentContractData(employer)?.minutes as number)}
                </td>
                <td>{currentRefreshPeriodEmployeesPurchased(employer)}</td>
                <td>
                  {currentRefreshPeriodEmployeesPurchased(employer) &&
                  employer.currentRefreshPeriod &&
                  employer.currentRefreshPeriod.billableClientCount >=
                    currentRefreshPeriodEmployeesPurchased(employer) ? (
                    <div className={styles.danger}>
                      <TextWithIcon
                        icon="exclamationTriangle"
                        text={intl.formatNumber(employer.currentRefreshPeriod.billableClientCount)}
                      />
                    </div>
                  ) : (
                    <>
                      {employer.currentRefreshPeriod?.billableClientCount && (
                        <FormattedNumber value={employer.currentRefreshPeriod.billableClientCount} />
                      )}
                    </>
                  )}
                  <Nbsp />
                  <FormattedMessage
                    id="models.refreshPeriod.billableCountsDisplay"
                    values={{
                      new: intl.formatNumber(employer.currentRefreshPeriod?.billableClientCountNew ?? 0),
                      refresh: intl.formatNumber(employer.currentRefreshPeriod?.billableClientCountRefresh ?? 0)
                    }}
                  />
                </td>
                <td>{employer.clientCount}</td>
                <td>
                  <DateStamp showWeekday={false} value={employer.createdAt} />
                </td>
                <td>
                  {employer.eligibilityMethod && (
                    <FormattedMessage id={`models.employer.eligibilityMethod.${employer.eligibilityMethod}`} />
                  )}
                </td>
                {!userIsExpert && (
                  <td>
                    <ActionIconButton
                      ariaLabelKey="accessibility.ariaLabels.edit"
                      icon="edit"
                      routerLink={`/admin/employers/${employer.id}/edit`}
                    />
                    {employer.eligibilityMethod === EligibilityMethodEnum.ELIGIBILITY_FILE && (
                      <>
                        <ActionIconButton
                          ariaLabelKey="accessibility.ariaLabels.eligibilityFiles"
                          hoverContent={<FormattedMessage id="pages.admin.employers.eligibilityFiles" />}
                          icon="file"
                          routerLink={`/admin/employers/${employer.id}/eligibility-files`}
                        />
                        <ActionIconButton
                          ariaLabelKey="accessibility.ariaLabels.eligibilityList"
                          hoverContent={<FormattedMessage id="pages.admin.employers.eligibilityList" />}
                          icon="users"
                          routerLink={`/admin/employers/${employer.id}/eligibility-list`}
                        />
                      </>
                    )}
                    <ActionIconButton
                      ariaLabelKey="accessibility.ariaLabels.qbr"
                      hoverContent={<FormattedMessage id="pages.admin.employerQbr.explanations.qbrExplainer" />}
                      icon="fileChartPie"
                      routerLink={`/admin/employers/${employer.id}/qbr`}
                    />
                    <ActionIconButton
                      ariaLabelKey="accessibility.ariaLabels.delete"
                      disabled={employer.clientCount !== 0}
                      hoverContent={
                        employer.clientCount !== 0 && (
                          <FormattedMessage
                            id="pages.admin.employers.delete.deleteEmployerDisabled"
                            values={{ name: employer.name }}
                          />
                        )
                      }
                      icon="trash"
                      onClick={generateDeleteStartHandler(employer)}
                    />
                  </td>
                )}
              </tr>
            ))}
          </Table>
        )}
      </HandleQuery>
      <DeleteConfirmationAlert
        messageKey="pages.admin.employers.reallyDelete"
        onDelete={handleDelete}
        opener={deleteOpener}
      />
    </>
  );
};

export default EmployersTable;
